import { Autocomplete, Button, ButtonGroup, TextField } from '@mui/material'
import dayjs from 'dayjs'
import { useAuth } from 'hooks'
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react'
import React, { useEffect, useMemo } from 'react'
import { funcionario } from 'service/api'
import { useStore } from 'store'
import { filterLocaisCentro } from './filter-select'
import { SelectProps } from './types'

type Props = SelectProps & {
    ativos?: boolean
    centroCustoId?: number
    localId?: number
    children?: never
}

export const SelectFuncionario: React.FC<Props> = ({
    size = 'small',
    width = 260,
    button: hasButtons = false,
    disabled,
    ativos,
    centroCustoId,
    localId,
    variant = 'filled',
}) => {
    const {
        decoded: { categoria },
    } = useAuth()
    const company = useStore(state => state.company)
    const employeeId = useStore(state => state.employeeId)
    const empresaId = useStore(state => state.companyId)
    const setEmployeeId = useStore(state => state.setEmployeeId)
    const selectedDate = useStore(state => state.selectedDate)

    const { data: funcionarios = [] } = funcionario.useFetch({
        queryParams: { empresaId },
        enabled: empresaId > 0,
    })

    // Filtra funcionários conforme regras de ativos, centro de custo e local
    const data = React.useMemo(
        () =>
            funcionarios.filter(obj => {
                if (ativos && obj.dtRescisao) {
                    const tolerancia = company?.diasRescisao ?? 0
                    return dayjs(obj.dtRescisao).isAfter(
                        dayjs(selectedDate).subtract(tolerancia, 'day'),
                    )
                }

                return filterLocaisCentro<typeof obj>(
                    !!localId && localId > 0 ? [localId] : [],
                    [centroCustoId!],
                    it => it,
                )(obj)
            }),
        [funcionarios, ativos, centroCustoId, localId, selectedDate],
    )

    const isPrimeiroFuncionario = useMemo(() => {
        if (data && data.length > 0) {
            return employeeId === data[0].id
        }
        return false
    }, [data, employeeId])

    const isUltimoFuncionario = useMemo(() => {
        if (data && data.length > 0) {
            return employeeId === data[data.length - 1].id
        }
        return false
    }, [data, employeeId])

    async function next() {
        data?.forEach((item, index) => {
            if (item.id === employeeId) {
                setEmployeeId(data[index + 1]?.id)
            }
        })
    }

    async function previous() {
        data?.forEach((item, index) => {
            if (item.id === employeeId) {
                setEmployeeId(data[index - 1]?.id)
            }
        })
    }

    // Busca o funcionário selecionado pelo id
    const selectedFuncionario = data.find(f => f.id === employeeId) || null

    useEffect(() => {
        if (employeeId > 0 && !selectedFuncionario && data.length) {
            setEmployeeId(data[0].id)
        }
    }, [selectedFuncionario, data, employeeId])

    // Renderização do componente
    return (
        <div className="flex items-center gap-2" key={selectedFuncionario?.id}>
            <Autocomplete
                className="flex-1"
                style={{ width }}
                options={data}
                getOptionLabel={option => option?.nome || ''}
                value={selectedFuncionario}
                onChange={(_, value) => {
                    if (value) setEmployeeId(value.id)
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                disabled={disabled || categoria === 5}
                renderInput={params => (
                    <TextField
                        {...params}
                        label="Funcionário"
                        size={size}
                        variant={variant}
                        className="w-full"
                        InputLabelProps={{ shrink: true }}
                        placeholder="Selecione um funcionário"
                    />
                )}
                noOptionsText={
                    <span className="text-gray-500 select-none">
                        Nenhum funcionário
                    </span>
                }
                clearIcon={null}
            />
            {hasButtons && categoria !== 5 && (
                <ButtonGroup variant="outlined" className="h-full">
                    <Button
                        onClick={previous}
                        disabled={isPrimeiroFuncionario}
                        className="w-12 min-w-0 px-0"
                    >
                        <ChevronLeftIcon />
                    </Button>
                    <Button
                        onClick={next}
                        disabled={isUltimoFuncionario}
                        className="w-12 min-w-0 px-0"
                    >
                        <ChevronRightIcon />
                    </Button>
                </ButtonGroup>
            )}
        </div>
    )
}
